import React, { useCallback, useEffect, useState } from "react";
import { Input, Form, AutoComplete, Modal } from "antd";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { useSocket } from "../../../context/SocketContext";
import GradientButton from "../UI/GradientButton";
import { storageService } from "../../../services/storageService";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import "./index.scss";

const E911Modal: React.FC = () => {
  const { socket } = useSocket();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<any[]>([]);
  const [locationDetails, setLocationDetails] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [e911Status, setE911Status] = useState<string | null>(null);

  const uid = storageService.getUdid();

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const response = await apiInstance.post(ENDPOINTS.EMERGENCY_911, values);
      if (response.status === 200) {
        toast.success("E911 address updated successfully!");
        form.resetFields();
        setSearchValue("");
        setShowModal(false);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to update E911 address. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (value: string) => {
    const selected = autoCompleteOptions.find((o) => o.value === value);
    if (!selected) return;

    try {
      const response = await apiInstance.post(ENDPOINTS.LOCATION_DETAIL, {
        placeId: selected.id,
        sessiontoken: uid,
      });
      setLocationDetails(response.data.data);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to fetch location details. Please try again."
      );
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) return;

    try {
      const response = await apiInstance.post(ENDPOINTS.LOCATION_SEARCH, {
        location: query,
        sessiontoken: uid,
      });
      const data = response.data.data.predictions || [];
      setAutoCompleteOptions(
        data.map((option: any) => ({
          id: option.place_id,
          label: option.description,
          value: option.description,
        }))
      );
    } catch (error: any) {
      toast.error(error?.message || "There was an error fetching locations");
    }
  };

  const debouncedSearch = useCallback(debounce(handleSearch, 500), []);

  useEffect(() => {
    if (!socket) return;

    socket.emit("e911Status");

    const onE911Status = (msg: any) => {
      const status = msg?.e911Status || null;
      setE911Status(status);

      if (!storageService.getAlreadyOpenedE911Once()) {
        setShowModal(true);
        storageService.setAlreadyOpenedE911Once();
      }
    };

    socket.on("e911Status", onE911Status);
    return () => {
      socket.off("e911Status", onE911Status);
    };
  }, [socket]);

  useEffect(() => {
    if (searchValue.trim()) {
      debouncedSearch(searchValue);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchValue, debouncedSearch]);

  useEffect(() => {
    if (locationDetails) {
      form.setFieldsValue({
        streetNumber: locationDetails.streetNumber || "",
        streetName: locationDetails.street || "",
        city: locationDetails.city || "",
        province: locationDetails.state || "",
        country: locationDetails.country || "",
        postalCode: locationDetails.zipCode || "",
      });
    }
  }, [locationDetails, form]);

  return (
    <Modal
      open={showModal}
      footer={null}
      closable={e911Status === "OPTIONAL"}
      onCancel={() => setShowModal(false)}
      className="custom-modal"
    >
      <div className="emergency-911-modal">
        <div className="form-container">
          <label className="autocomplete-label">Search Location</label>
          <AutoComplete
            className="autocomplete"
            value={searchValue}
            options={autoCompleteOptions}
            style={{ width: "100%" }}
            onChange={setSearchValue}
            onSelect={handleSelect}
            placeholder="Search location"
          />

          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label="Street Number"
              name="streetNumber"
              rules={[{ required: true, message: "Street number is required" }]}
            >
              <Input
                placeholder="Enter street number"
                size="large"
                className="custom-input"
              />
            </Form.Item>

            <Form.Item
              label="Street Name"
              name="streetName"
              rules={[{ required: true, message: "Street name is required" }]}
            >
              <Input
                placeholder="Enter street name"
                size="large"
                className="custom-input"
              />
            </Form.Item>

            <div className="form-row">
              <Form.Item
                label="City"
                name="city"
                className="form-half"
                rules={[{ required: true, message: "City is required" }]}
              >
                <Input
                  placeholder="Select city"
                  size="large"
                  className="custom-input"
                />
              </Form.Item>

              <Form.Item
                label="Province"
                name="province"
                className="form-half"
                rules={[{ required: true, message: "Province is required" }]}
              >
                <Input
                  placeholder="Select province"
                  size="large"
                  className="custom-input"
                />
              </Form.Item>
            </div>

            <div className="form-row">
              <Form.Item
                label="Country"
                name="country"
                className="form-half"
                rules={[{ required: true, message: "Country is required" }]}
              >
                <Input
                  placeholder="Select country"
                  size="large"
                  className="custom-input"
                />
              </Form.Item>

              <Form.Item
                label="Postal Code"
                name="postalCode"
                className="form-half"
                rules={[{ required: true, message: "Postal code is required" }]}
              >
                <Input
                  placeholder="Enter postal code"
                  size="large"
                  className="custom-input"
                />
              </Form.Item>
            </div>

            <Form.Item className="submit-btn">
              <GradientButton htmlType="submit" loading={loading}>
                Submit
              </GradientButton>
            </Form.Item>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default E911Modal;
